import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatIconArrowBack } from "blocks-nextjs/blocks-react/components/Icons/ArrowBack/ArrowBack";
import { CatIconArrowForward } from "blocks-nextjs/blocks-react/components/Icons/ArrowForward/ArrowForward";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { format } from "date-fns";
import Plotly from "plotly.js-dist-min";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TimelineVerticalLine from "../../assets/TimelineVerticalLine.svg";
import EssHistoricalEmptyState from "../../pages/Projects/EssHistoricalEmptyState";
import { useAssetPowerSocTrendsV2Query, useEssAssetEventsRangeQuery } from "../../services/query/project-asset-details.query";
const SOC_MAX = 100;
const SOC_Y_TICKS = [0, 20, 40, 60, 80, 100];
const SOC_Y_TICK_LABELS = SOC_Y_TICKS.map((tick) => `${tick}%`);
const SOC_LINE_SMOOTHING = 1.3;
const OPERATIONS_DOMAIN_TOP = 0.08;
const SOC_DOMAIN_BOTTOM = 0.18;
const SOC_LINE_WIDTH = 2;
const SOC_MARKER_SIZE = 4;
// Matches the Plotly layout's margin.t/margin.b so the hover guide line lines up with the
// chart's actual plot area instead of overflowing above/below it.
const CHART_VERTICAL_MARGIN = 10;
// Visible boundary dots at not-reporting transitions (hollow white "stopped" / filled
// black "resumed"), plus the ring width for the hollow circle.
const SOC_BREAK_MARKER_SIZE = 8;
const SOC_BREAK_MARKER_RING = 1.5;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAY_BAND_COLOR = "#F2F2F2";
const MAX_RANGE_DAYS = 31;
const DAYS_PER_VIEW = 7;
const CHARGING_COLOR = "#1AC69E";
const DISCHARGING_COLOR = "#FB5A00";
const NOT_REPORTING_LINE_COLOR = "#CCCCCC";
// Rough width used to decide whether the hover tooltip fits to the right of the cursor
// before it's actually rendered (avoids a measure-then-reposition flicker).
const TOOLTIP_ESTIMATED_WIDTH = 220;
const TOOLTIP_GAP = 12;
// Trackpad wheel deltas accumulate until roughly one day of horizontal pan is
// reached. This smooths navigation across devices with different wheel values.
const WHEEL_DAY_THRESHOLD = 60;
/**
 * Operations state colour map, mirroring ActivityTimeline's getShapeColor so the ESS SoC /
 * Operations visual stays consistent with the rest of the app. `match` holds the
 * lower-cased activity/status spellings the backend may send for each state. `color` feeds
 * Plotly (which needs a raw hex); `swatch` is the Tailwind class for the DOM legend.
 */
const OPERATION_STATE_COLORS = [
    { color: "#1AC69E", label: "Charging", match: ["charging"], swatch: "bg-[#1AC69E]" },
    { color: "#91F2E6", label: "AC Charging", match: ["ac_charging", "ac charging"], swatch: "bg-[#91F2E6]" },
    { color: "#078878", label: "DC Charging", match: ["dc_charging", "dc charging"], swatch: "bg-[#078878]" },
    { color: "#FB5A00", label: "Discharging", match: ["discharging"], swatch: "bg-[#FB5A00]" },
    { color: "#0066FF", label: "Working", match: ["working"], swatch: "bg-[#0066FF]" },
    { color: "#FF8946", label: "Idle", match: ["idle", "available", "key_off"], swatch: "bg-[#FF8946]" },
    {
        color: "#FFA230",
        label: "Self Discharge",
        match: ["self discharge", "self_discharge"],
        swatch: "bg-[#FFA230]"
    },
    { color: "#8CBAFF", label: "Tracking", match: ["tracking"], swatch: "bg-[#8CBAFF]" },
    { color: "#6852BE", label: "Transported", match: ["transported", "transporting"], swatch: "bg-[#6852BE]" },
    {
        color: "#909090",
        label: "Trickle",
        match: ["trickle", "trickle charge", "trickle_charge", "trickle charging", "trickle_charging"],
        swatch: "bg-[#909090]"
    },
    {
        color: "#808080",
        label: "Not Reporting",
        match: ["not reporting", "notreporting", "noactivitylogged"],
        swatch: "bg-[#808080]"
    }
];
/** Resolve an activity/status string to its state colour (falls back to grey). */
const getOperationColor = (state) => {
    const key = (state ?? "").toLowerCase().trim();
    const found = OPERATION_STATE_COLORS.find((entry) => entry.match.includes(key));
    return found ? found.color : "#808080";
};
/** Lower-cased activity/status spellings that mean the asset was not reporting. */
const NOT_REPORTING_MATCH = new Set(["not reporting", "notreporting", "noactivitylogged"]);
/** Whether an activity/status string represents a "not reporting" period. */
const isNotReportingState = (state) => NOT_REPORTING_MATCH.has((state ?? "").toLowerCase().trim());
/** Format an activity's duration (ms) as e.g. "2 Hours, 45 Minutes". */
const formatOperationDuration = (durationMs) => {
    const totalMinutes = Math.max(0, Math.round(durationMs / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts = [];
    if (hours > 0)
        parts.push(`${hours} ${hours === 1 ? "Hour" : "Hours"}`);
    parts.push(`${minutes} ${minutes === 1 ? "Minute" : "Minutes"}`);
    return parts.join(", ");
};
/** Legend shown beneath the Operations strip (mockup order; excludes Not Reporting). */
const OPERATIONS_LEGEND = OPERATION_STATE_COLORS.filter((entry) => entry.label !== "Not Reporting");
const hasFiniteSoc = (value) => typeof value === "number" && Number.isFinite(value);
/** Colour a SoC line segment by whether charge is rising (charging) or falling
 *  (discharging), mirroring the mockup and avoiding any backend power-sign guess. */
const directionColor = (fromSoc, toSoc) => {
    return toSoc >= fromSoc ? CHARGING_COLOR : DISCHARGING_COLOR;
};
// Always use the mounted chart ref. Avoid document.getElementById(chartId),
// which can resolve to a stale node from another drawer instance and break
// Plotly relayout calls.
const applyChartDoubleClick = ({ dayRangeEnd, dayRangeStart, graphDiv, isFullRangeVisible, isPlotReady, pagedRangeEnd, pagedRangeStart, setIsFullRangeVisible, usesFullRangeDoubleClick }) => {
    if (!isPlotReady ||
        !pagedRangeStart ||
        !pagedRangeEnd ||
        !graphDiv?.isConnected ||
        !graphDiv._fullLayout?.xaxis ||
        !graphDiv._fullLayout.yaxis) {
        return;
    }
    const currentXRange = graphDiv._fullLayout.xaxis.range;
    const currentYRange = graphDiv._fullLayout.yaxis.range;
    const expectedXRange = isFullRangeVisible && dayRangeStart && dayRangeEnd
        ? [dayRangeStart, dayRangeEnd]
        : [pagedRangeStart, pagedRangeEnd];
    const xRangeMatches = currentXRange.every((value, index) => Math.abs(new Date(value).getTime() - new Date(expectedXRange[index]).getTime()) < 1);
    const yRangeIsDefault = Math.abs(currentYRange[0]) < Number.EPSILON && Math.abs(currentYRange[1] - SOC_MAX) < Number.EPSILON;
    if (!xRangeMatches || !yRangeIsDefault) {
        setIsFullRangeVisible(false);
        Plotly.relayout(graphDiv, {
            "xaxis.range": [pagedRangeStart, pagedRangeEnd],
            "yaxis.autorange": false,
            "yaxis.range": [0, SOC_MAX]
        });
        return;
    }
    if (usesFullRangeDoubleClick) {
        setIsFullRangeVisible((isVisible) => !isVisible);
        return;
    }
    Plotly.relayout(graphDiv, {
        "xaxis.range": [pagedRangeStart, pagedRangeEnd],
        "yaxis.autorange": true
    });
};
/** Whether the SoC-line edge between two trend points is a not-reporting span. */
const isNotReportingEdge = (from, to, segments) => {
    const fromSeg = from.segIndex !== undefined ? segments[from.segIndex] : undefined;
    if (!fromSeg)
        return false;
    if (from.segIndex === to.segIndex)
        return isNotReportingState(fromSeg.activity ?? fromSeg.assetStatus);
    const toSeg = to.segIndex !== undefined ? segments[to.segIndex] : undefined;
    if (!toSeg)
        return false;
    // Bridging edge between two segments — dotted only when there is a real time gap.
    return new Date(toSeg.start).getTime() > new Date(fromSeg.end).getTime();
};
/** Split SoC trend points into styled runs: solid and direction-coloured while reporting,
 *  or a dotted grey line across not-reporting periods. Consecutive edges sharing the same
 *  colour + dash are merged into a single run. */
const buildSocRuns = (trendPoints, segments) => {
    const socRuns = [];
    for (let i = 0; i < trendPoints.length - 1; i += 1) {
        const from = trendPoints[i];
        const to = trendPoints[i + 1];
        const notReporting = isNotReportingEdge(from, to, segments);
        const color = notReporting ? NOT_REPORTING_LINE_COLOR : directionColor(from.soc, to.soc);
        const dash = notReporting ? "dot" : "solid";
        const lastRun = socRuns.at(-1);
        if (lastRun?.color === color && lastRun?.dash === dash) {
            lastRun.x.push(to.timestamp);
            lastRun.y.push(to.soc);
        }
        else {
            socRuns.push({ color, dash, x: [from.timestamp, to.timestamp], y: [from.soc, to.soc] });
        }
    }
    return socRuns;
};
/** Whether the selected range extends past the data at its start (leading) and/or end
 *  (trailing) — i.e. edge spans with no reporting that should still read as not-reporting. */
const rangeGapFlags = (trendPoints, range) => {
    const first = trendPoints[0];
    const last = trendPoints.at(-1);
    if (!range || !first || !last)
        return { leading: false, trailing: false };
    return {
        leading: new Date(range[0]).getTime() < new Date(first.timestamp).getTime(),
        trailing: new Date(range[1]).getTime() > new Date(last.timestamp).getTime()
    };
};
/** Extend the SoC runs with a flat dotted "not reporting" segment across any leading/trailing
 *  span of the selected range that has no reporting data, so those days read as not-reporting
 *  instead of the line simply stopping at the first/last data point. */
const withRangeNotReporting = (socRuns, trendPoints, range, gaps) => {
    if (!range)
        return socRuns;
    const first = trendPoints[0];
    const last = trendPoints.at(-1);
    if (!first || !last)
        return socRuns;
    const runs = [...socRuns];
    if (gaps.leading) {
        runs.unshift({
            color: NOT_REPORTING_LINE_COLOR,
            dash: "dot",
            x: [range[0], first.timestamp],
            y: [first.soc, first.soc]
        });
    }
    if (gaps.trailing) {
        runs.push({
            color: NOT_REPORTING_LINE_COLOR,
            dash: "dot",
            x: [last.timestamp, range[1]],
            y: [last.soc, last.soc]
        });
    }
    return runs;
};
/** Per-figma the SoC line only shows a marker at not-reporting transitions: a hollow white
 *  circle where reporting stops (start of a dotted gap) and a filled black circle where it
 *  resumes (end of a dotted gap). Every other point stays transparent so it can still act as
 *  an invisible hover target without cluttering the line. Leading/trailing range gaps also
 *  mark the first/last point (resume/stop respectively). */
const buildSocMarkers = (trendPoints, segments, gaps) => {
    const colors = [];
    const lineColors = [];
    const lineWidths = [];
    const sizes = [];
    const lastIdx = trendPoints.length - 1;
    trendPoints.forEach((point, idx) => {
        const resumes = (idx > 0 && isNotReportingEdge(trendPoints[idx - 1], point, segments)) || (idx === 0 && gaps.leading);
        const stops = (idx < lastIdx && isNotReportingEdge(point, trendPoints[idx + 1], segments)) ||
            (idx === lastIdx && gaps.trailing);
        if (resumes) {
            colors.push("#000000");
            lineColors.push("#000000");
            lineWidths.push(0);
            sizes.push(SOC_BREAK_MARKER_SIZE);
        }
        else if (stops) {
            colors.push("#FFFFFF");
            lineColors.push(NOT_REPORTING_LINE_COLOR);
            lineWidths.push(SOC_BREAK_MARKER_RING);
            sizes.push(SOC_BREAK_MARKER_SIZE);
        }
        else {
            colors.push("rgba(0,0,0,0)");
            lineColors.push("rgba(0,0,0,0)");
            lineWidths.push(0);
            sizes.push(SOC_MARKER_SIZE);
        }
    });
    return { colors, lineColors, lineWidths, sizes };
};
/** Expand a from/to range into a capped list of yyyy-MM-dd day strings. */
const toDayList = (fromDate, toDate) => {
    if (!fromDate || !toDate)
        return [];
    // The range strings encode project-timezone midnight, so the leading YYYY-MM-DD is the
    // project calendar day. Enumerate those calendar days (parsed as UTC to avoid tz drift).
    const start = new Date(`${fromDate.slice(0, 10)}T00:00:00Z`).getTime();
    const end = new Date(`${toDate.slice(0, 10)}T00:00:00Z`).getTime();
    if (Number.isNaN(start) || Number.isNaN(end))
        return [];
    const days = [];
    for (let time = start; time <= end && days.length < MAX_RANGE_DAYS; time += MS_PER_DAY) {
        days.push(new Date(time).toISOString().slice(0, 10));
    }
    return days;
};
const renderChartStatus = ({ hasData, height, isError, isLoading }) => {
    if (isLoading) {
        return (_jsx("div", { className: "flex items-center justify-center h-full min-h-[40vh]", "data-test": "ess-soc-operations-loading", children: _jsx(CatLoadingIndicator, {}) }));
    }
    if (isError) {
        return (_jsx("div", { className: "flex items-center justify-center w-full", "data-test": "ess-soc-operations-error-state", style: { minHeight: height }, children: _jsx(EssHistoricalEmptyState, { description: "SoC/energy history could not be loaded for this asset. Please try again later." }) }));
    }
    if (!hasData) {
        return (_jsx("div", { className: "flex items-center justify-center w-full", "data-test": "ess-soc-operations-empty-state", style: { minHeight: height }, children: _jsx(EssHistoricalEmptyState, { description: "No SoC/energy history is available for this asset in the selected range." }) }));
    }
    return null;
};
/**
 * ESS "State of Charge Tracking" visual (US4) — designer mockup parity.
 *
 * Two synchronised panels sharing one time axis across the selected Historical Info
 * date range:
 *  - Top: a dense State-of-Charge (%) telemetry line (per-day trends, merged); each
 *    segment is green while charge rises (charging) and orange while it falls
 *    (discharging).
 *  - Bottom: an "Operations" strip of state-coloured blocks (per-day activity segments).
 *
 * The backing endpoint accepts a timestamp-range filter, so the whole selected range is
 * fetched in a single request rather than one call per day.
 */
const SocOperationsChart = ({ assetId, enabled = true, fromDate, height, projectId, toDate }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const chartId = `ess-soc-operations-${assetId || "chart"}`;
    const [tooltip, setTooltip] = useState(null);
    const [hasPlotError, setHasPlotError] = useState(false);
    const days = useMemo(() => toDayList(fromDate, toDate), [fromDate, toDate]);
    const canQuery = Boolean(enabled && assetId && projectId && days.length > 0);
    useEffect(() => setHasPlotError(false), [assetId, fromDate, toDate]);
    const activityQuery = useEssAssetEventsRangeQuery(projectId, assetId, fromDate, toDate, canQuery);
    const trendsQuery = useAssetPowerSocTrendsV2Query(projectId, assetId, fromDate, toDate);
    const segments = useMemo(() => {
        const acts = activityQuery.data?.assetChargeActivities ?? [];
        const collected = [];
        acts.forEach((seg) => {
            if (seg.activityStartTimeStamp && seg.activityEndTimeStamp) {
                collected.push({
                    activity: seg.activity,
                    assetStatus: seg.assetStatus,
                    end: seg.activityEndTimeStamp,
                    endSoc: seg.endSoc,
                    start: seg.activityStartTimeStamp,
                    startSoc: seg.startSoc
                });
            }
        });
        collected.sort((first, second) => new Date(first.start).getTime() - new Date(second.start).getTime());
        return collected;
    }, [activityQuery.data]);
    // SoC telemetry points returned by the trends endpoint for the selected asset and
    // date range. These points drive the SoC line, while charging/discharging state
    // and line colouring are derived later from the change between consecutive values.
    const trendPoints = useMemo(() => {
        const assetTrend = trendsQuery.data?.assetTrendsDetails?.find((detail) => detail.assetId === assetId);
        return (assetTrend?.trends
            ?.filter((trend) => hasFiniteSoc(trend.soc) && trend.timestamp)
            .map((trend) => ({
            soc: trend.soc,
            timestamp: trend.timestamp
        })) ?? []);
    }, [trendsQuery.data, assetId]);
    // Charging state per trend point, derived from the SoC slope so the custom hover
    // tooltip's "Charging State" row always matches the SoC line's rise/fall colour.
    const markerStates = useMemo(() => trendPoints.map((point, idx) => {
        const previous = trendPoints[idx - 1];
        if (previous && point.soc > previous.soc)
            return "Charging";
        if (previous && point.soc < previous.soc)
            return "Discharging";
        return "Idle";
    }), [trendPoints]);
    // Refs allow long-lived Plotly/native event handlers to read current data
    // without being detached and reattached on every render.
    const trendPointsRef = useRef(trendPoints);
    trendPointsRef.current = trendPoints;
    const markerStatesRef = useRef(markerStates);
    markerStatesRef.current = markerStates;
    const segmentsRef = useRef(segments);
    segmentsRef.current = segments;
    // Custom hover tooltip (figma parity — Plotly's native hoverlabel can't control
    // padding/typography/arrow precisely enough). Driven by a native mousemove listener on
    // the plot (rather than plotly_hover, which only fires directly over a marker) so the
    // tooltip + vertical guide line appear anywhere the user hovers over the chart. The
    // cursor's X is mapped to data via Plotly's rendered axis geometry, then snapped to the
    // nearest trend point in time.
    const handlePlotMouseMove = useCallback((clientX, clientY) => {
        const points = trendPointsRef.current;
        if (points.length === 0)
            return;
        const containerBounds = containerRef.current?.getBoundingClientRect();
        // Read from the mounted chart ref rather than resolving by id to avoid
        // accidentally targeting a stale chart instance.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const graphDiv = chartRef.current;
        const xaxis = graphDiv?._fullLayout?.xaxis;
        const yaxis = graphDiv?._fullLayout?.yaxis;
        const yaxis2 = graphDiv?._fullLayout?.yaxis2;
        if (!containerBounds || !xaxis || !yaxis)
            return;
        const plotLeft = xaxis._offset;
        const plotWidth = xaxis._length;
        const [rangeStart, rangeEnd] = xaxis.range.map((value) => new Date(value).getTime());
        // Only react while the cursor is horizontally within the plotting area — hovering the
        // y-axis labels / margins to the left or right must not show the guide line or tooltips.
        const frac = (clientX - containerBounds.left - plotLeft) / plotWidth;
        if (frac < 0 || frac > 1) {
            setTooltip(null);
            return;
        }
        // Same for vertical bounds: the plotted area spans the SoC panel's top down to the
        // bottom of the Operations strip, so the x-axis labels and surrounding whitespace
        // must not trigger the tooltip either.
        const plotTop = yaxis._offset;
        const plotBottom = yaxis2 && typeof yaxis2._offset === "number"
            ? yaxis2._offset + yaxis2._length
            : plotTop + yaxis._length;
        const cursorY = clientY - containerBounds.top;
        if (cursorY < plotTop || cursorY > plotBottom) {
            setTooltip(null);
            return;
        }
        const cursorMs = rangeStart + frac * (rangeEnd - rangeStart);
        let nearestIdx = 0;
        let nearestDist = Infinity;
        points.forEach((candidate, idx) => {
            const dist = Math.abs(new Date(candidate.timestamp).getTime() - cursorMs);
            if (dist < nearestDist) {
                nearestDist = dist;
                nearestIdx = idx;
            }
        });
        const trendPoint = points[nearestIdx];
        const left = clientX - containerBounds.left;
        // Anchor the banner vertically to the SoC curve (the point's y value) rather than the
        // mouse, so it hugs the line while still following the cursor horizontally.
        const [yStart, yEnd] = yaxis.range;
        const yTop = yaxis._offset;
        const yLength = yaxis._length;
        const socFrac = Math.min(1, Math.max(0, (trendPoint.soc - yStart) / (yEnd - yStart)));
        const top = yTop + (1 - socFrac) * yLength;
        // Flip to sit to the left of the point (arrow on the right) if it wouldn't fit to the right.
        const placement = left + TOOLTIP_GAP + TOOLTIP_ESTIMATED_WIDTH > containerBounds.width ? "left" : "right";
        // Bottom banner: the operations segment whose time span contains the cursor. Its
        // vertical anchor is the middle of the operations strip (y2 axis geometry).
        const activeSegment = segmentsRef.current.find((segment) => {
            const segmentStart = new Date(segment.start).getTime();
            const segmentEnd = new Date(segment.end).getTime();
            return cursorMs >= segmentStart && cursorMs <= segmentEnd;
        });
        const opsTop = yaxis2 && typeof yaxis2._offset === "number" ? yaxis2._offset + yaxis2._length / 2 : top;
        let opsTitle;
        let opsStarted;
        let opsDuration;
        // Asset State and Utilization are inferred from the active operations
        // segment. Segments map to On/Off + Working; periods with no active
        // segment display "--".
        let assetState = "--";
        let utilization = "--";
        if (activeSegment) {
            const segmentStart = new Date(activeSegment.start);
            const segmentEnd = new Date(activeSegment.end);
            const status = (activeSegment.assetStatus ?? activeSegment.activity ?? "").trim();
            opsTitle = "Working";
            opsStarted = `${format(segmentStart, "HH:mm")} - ${format(segmentEnd, "HH:mm")}`;
            opsDuration = formatOperationDuration(segmentEnd.getTime() - segmentStart.getTime());
            utilization = "Working";
            assetState = status.toLowerCase() === "off" ? "Off" : "On";
        }
        setTooltip({
            assetState,
            left,
            opsDuration,
            opsStarted,
            opsTitle,
            opsTop,
            placement,
            soc: trendPoint.soc,
            state: markerStatesRef.current[nearestIdx] ?? "Idle",
            timestamp: trendPoint.timestamp,
            top,
            utilization
        });
    }, [chartId]);
    // Only treat the chart as having data when Plotly can render at least one
    // SoC point or a positive-duration operations segment. This prevents an
    // empty plot area from rendering when the source data contains only
    // malformed or zero-duration records.
    const hasData = useMemo(() => {
        if (trendPoints.length > 0)
            return true;
        return segments.some((seg) => {
            const start = new Date(seg.start).getTime();
            const end = new Date(seg.end).getTime();
            return Number.isFinite(start) && Number.isFinite(end) && end > start;
        });
    }, [trendPoints, segments]);
    const handleUnhover = useCallback(() => setTooltip(null), []);
    const isLoading = canQuery && (activityQuery.isLoading || trendsQuery.isLoading);
    const isError = hasPlotError || trendsQuery.isError || (canQuery && activityQuery.isError);
    // Alternating day background bands + the date tick positions. Each band is centred on a
    // day's midnight (spanning the surrounding noon-to-noon window) so the date label — which
    // sits at midnight — lands in the middle of its patch rather than on the boundary.
    const dayLayout = useMemo(() => {
        const halfDay = MS_PER_DAY / 2;
        const dd = (value) => `${String(new Date(value).getUTCDate()).padStart(2, "0")}/${String(new Date(value).getUTCMonth() + 1).padStart(2, "0")}`;
        const empty = {
            bands: [],
            range: null,
            starts: [],
            tickDates: [],
            tickText: [],
            ticks: []
        };
        // Preferred path: derive day boundaries directly from the selected range. The range
        // strings encode project-timezone midnight (e.g. "…T00:00:00.000+02:00"), so the leading
        // YYYY-MM-DD is the project calendar day and each following midnight is +24h in real
        // (UTC) time. Ticks are positioned at those real instants (so they line up with segment
        // timestamps) while the labels come from the project calendar date — Plotly formats date
        // axes in UTC, which would otherwise mislabel and mis-count positive-offset timezones.
        const fromCal = fromDate?.slice(0, 10).split("-").map(Number);
        if (fromDate && toDate && fromCal?.length === 3 && fromCal.every((n) => Number.isFinite(n))) {
            const fromInstant = new Date(fromDate).getTime();
            const toInstant = new Date(toDate).getTime();
            if (Number.isNaN(fromInstant) || Number.isNaN(toInstant))
                return empty;
            const [fy, fm, fd] = fromCal;
            const numDays = Math.min(MAX_RANGE_DAYS, Math.max(1, Math.floor((toInstant - fromInstant) / MS_PER_DAY) + 1));
            const bands = [];
            const starts = [];
            const ticks = [];
            const tickText = [];
            const tickDates = [];
            for (let k = 0; k < numDays; k += 1) {
                const instant = fromInstant + k * MS_PER_DAY;
                starts.push(new Date(instant).toISOString());
                ticks.push(new Date(instant + halfDay).toISOString());
                const cal = new Date(Date.UTC(fy, fm - 1, fd + k));
                tickText.push(dd(cal.getTime()));
                tickDates.push(cal);
                if (k % 2 === 1) {
                    bands.push({
                        x0: new Date(instant).toISOString(),
                        x1: new Date(instant + MS_PER_DAY).toISOString()
                    });
                }
            }
            const range = [new Date(fromInstant).toISOString(), new Date(toInstant).toISOString()];
            return { bands, range, starts, tickDates, tickText, ticks };
        }
        // Fallback (no explicit range): span the data extent, labelling ticks by their UTC date.
        const times = [
            ...trendPoints.map((point) => new Date(point.timestamp).getTime()),
            ...segments.flatMap((seg) => [new Date(seg.start).getTime(), new Date(seg.end).getTime()])
        ].filter((value) => Number.isFinite(value));
        if (times.length === 0)
            return empty;
        const firstDay = new Date(Math.min(...times));
        firstDay.setUTCHours(0, 0, 0, 0);
        const max = Math.max(...times);
        const bands = [];
        const starts = [];
        const ticks = [];
        const tickText = [];
        const tickDates = [];
        let dayStart = firstDay.getTime();
        let index = 0;
        while (dayStart < max + halfDay && ticks.length < MAX_RANGE_DAYS) {
            starts.push(new Date(dayStart).toISOString());
            ticks.push(new Date(dayStart + halfDay).toISOString());
            tickText.push(dd(dayStart));
            tickDates.push(new Date(dayStart));
            if (index % 2 === 1) {
                bands.push({
                    x0: new Date(dayStart).toISOString(),
                    x1: new Date(dayStart + MS_PER_DAY).toISOString()
                });
            }
            dayStart += MS_PER_DAY;
            index += 1;
        }
        const range = [firstDay.toISOString(), new Date(max).toISOString()];
        return { bands, range, starts, tickDates, tickText, ticks };
    }, [trendPoints, segments, fromDate, toDate]);
    const dayBands = dayLayout.bands;
    const dayTicks = dayLayout.ticks;
    const dayStarts = dayLayout.starts;
    const dayTickText = dayLayout.tickText;
    const dayTickDates = dayLayout.tickDates;
    const dayRange = dayLayout.range;
    // Page through the selected range in fixed 7-day windows via explicit Previous/Next Week
    // controls (figma parity) instead of a scrollbar. Multi-week ranges expose complete weeks
    // only; otherwise the oldest remainder becomes a misleading one-day zoom page.
    const numDays = dayTicks.length;
    const usesFullRangeDoubleClick = numDays > DAYS_PER_VIEW;
    const weekChunks = useMemo(() => {
        if (numDays <= DAYS_PER_VIEW) {
            return numDays > 0 ? [{ endIdx: numDays - 1, startIdx: 0 }] : [];
        }
        const chunks = [];
        let idx = numDays % DAYS_PER_VIEW;
        while (idx < numDays) {
            chunks.push({ endIdx: idx + DAYS_PER_VIEW - 1, startIdx: idx });
            idx += DAYS_PER_VIEW;
        }
        return chunks;
    }, [numDays]);
    const totalWeeks = weekChunks.length;
    const [viewportStartIdx, setViewportStartIdx] = useState(0);
    const [isFullRangeVisible, setIsFullRangeVisible] = useState(false);
    const rangeSignatureRef = useRef("");
    const wheelDeltaRef = useRef(0);
    const wheelDirectionRef = useRef(0);
    const resetWheelDelta = useCallback(() => {
        wheelDeltaRef.current = 0;
        wheelDirectionRef.current = 0;
    }, []);
    // Skip any leading partial-week remainder. Week navigation exposes complete
    // 7-day windows only.
    const minimumViewportStartIdx = numDays > DAYS_PER_VIEW ? numDays % DAYS_PER_VIEW : 0;
    const maxViewportStartIdx = Math.max(0, numDays - DAYS_PER_VIEW);
    useEffect(() => {
        const signature = `${fromDate ?? ""}|${toDate ?? ""}`;
        if (signature !== rangeSignatureRef.current) {
            rangeSignatureRef.current = signature;
            resetWheelDelta();
            setIsFullRangeVisible(false);
            setViewportStartIdx(0);
        }
    }, [fromDate, toDate, resetWheelDelta]);
    const clampedViewportStartIdx = Math.min(maxViewportStartIdx, Math.max(minimumViewportStartIdx, viewportStartIdx));
    const currentWeekIndex = Math.min(totalWeeks - 1, Math.floor((clampedViewportStartIdx - minimumViewportStartIdx) / DAYS_PER_VIEW));
    const canGoPrevWeek = clampedViewportStartIdx > minimumViewportStartIdx;
    const canGoNextWeek = clampedViewportStartIdx < maxViewportStartIdx;
    const goToPrevWeek = useCallback(() => {
        resetWheelDelta();
        setIsFullRangeVisible(false);
        setViewportStartIdx((idx) => Math.max(minimumViewportStartIdx, idx - DAYS_PER_VIEW));
    }, [minimumViewportStartIdx, resetWheelDelta]);
    const goToNextWeek = useCallback(() => {
        resetWheelDelta();
        setIsFullRangeVisible(false);
        setViewportStartIdx((idx) => Math.min(maxViewportStartIdx, idx + DAYS_PER_VIEW));
    }, [maxViewportStartIdx, resetWheelDelta]);
    const handleChartWheel = useCallback((event) => {
        if (totalWeeks <= 1 || Math.abs(event.deltaX) <= Math.abs(event.deltaY))
            return;
        event.preventDefault();
        const direction = Math.sign(event.deltaX);
        if (wheelDirectionRef.current !== 0 && wheelDirectionRef.current !== direction) {
            wheelDeltaRef.current = 0;
        }
        wheelDirectionRef.current = direction;
        wheelDeltaRef.current += event.deltaX;
        const dayShift = Math.trunc(wheelDeltaRef.current / WHEEL_DAY_THRESHOLD);
        if (dayShift === 0)
            return;
        wheelDeltaRef.current -= dayShift * WHEEL_DAY_THRESHOLD;
        setIsFullRangeVisible(false);
        setViewportStartIdx((idx) => Math.min(maxViewportStartIdx, Math.max(minimumViewportStartIdx, idx + dayShift)));
    }, [maxViewportStartIdx, minimumViewportStartIdx, totalWeeks]);
    const pagedRange = useMemo(() => {
        if (!dayRange || weekChunks.length === 0)
            return dayRange ? [...dayRange] : null;
        const endIdx = Math.min(clampedViewportStartIdx + DAYS_PER_VIEW - 1, numDays - 1);
        const startMs = new Date(dayStarts[clampedViewportStartIdx]).getTime();
        const selectedEndMs = toDate ? new Date(toDate).getTime() : Number.NaN;
        const endMs = endIdx === numDays - 1 && Number.isFinite(selectedEndMs)
            ? selectedEndMs
            : new Date(dayStarts[endIdx]).getTime() + MS_PER_DAY;
        return [new Date(startMs).toISOString(), new Date(endMs).toISOString()];
    }, [clampedViewportStartIdx, dayRange, dayStarts, numDays, toDate, weekChunks]);
    const windowRange = isFullRangeVisible && dayRange ? [...dayRange] : pagedRange;
    const windowRangeStart = windowRange?.[0];
    const windowRangeEnd = windowRange?.[1];
    // Plotly initialization is async. Keep the latest viewport range in a ref so
    // a delayed Plotly.newPlot completion applies the current range rather than
    // the range captured when rendering started.
    const latestWindowRangeRef = useRef(null);
    latestWindowRangeRef.current =
        windowRangeStart && windowRangeEnd ? [windowRangeStart, windowRangeEnd] : null;
    const pagedRangeStart = pagedRange?.[0];
    const pagedRangeEnd = pagedRange?.[1];
    const dayRangeStart = dayRange?.[0];
    const dayRangeEnd = dayRange?.[1];
    // "Week X of Y  •  MMM d - MMM d" label shown between the Previous/Next Week controls.
    const weekRangeLabel = useMemo(() => {
        if (weekChunks.length === 0)
            return "";
        const monthDay = (date) => new Intl.DateTimeFormat("en-US", { day: "numeric", month: "short", timeZone: "UTC" }).format(date);
        if (isFullRangeVisible) {
            return `Full Range  •  ${monthDay(dayTickDates[0])} - ${monthDay(dayTickDates.at(-1))}`;
        }
        const endIdx = Math.min(clampedViewportStartIdx + DAYS_PER_VIEW - 1, numDays - 1);
        const startLabel = monthDay(dayTickDates[clampedViewportStartIdx]);
        const endLabel = monthDay(dayTickDates[endIdx]);
        return `Week ${currentWeekIndex + 1} of ${weekChunks.length}  •  ${startLabel} - ${endLabel}`;
    }, [clampedViewportStartIdx, currentWeekIndex, dayTickDates, isFullRangeVisible, numDays, weekChunks]);
    const isPlotReadyRef = useRef(false);
    const handleChartDoubleClick = useCallback(() => {
        applyChartDoubleClick({
            dayRangeEnd,
            dayRangeStart,
            graphDiv: chartRef.current,
            isFullRangeVisible,
            isPlotReady: isPlotReadyRef.current,
            pagedRangeEnd,
            pagedRangeStart,
            setIsFullRangeVisible,
            usesFullRangeDoubleClick
        });
    }, [dayRangeEnd, dayRangeStart, isFullRangeVisible, pagedRangeEnd, pagedRangeStart, usesFullRangeDoubleClick]);
    const plotSignatureRef = useRef("");
    // Guards against async Plotly race conditions. If an older Plotly.newPlot()
    // resolves after a newer render has already started, its listeners and state
    // updates must be ignored.
    const plotGenerationRef = useRef(0);
    const resizeObserverRef = useRef(null);
    const hoverCleanupRef = useRef(null);
    const chartWheelRef = useRef(handleChartWheel);
    chartWheelRef.current = handleChartWheel;
    const chartDoubleClickRef = useRef(handleChartDoubleClick);
    chartDoubleClickRef.current = handleChartDoubleClick;
    useEffect(() => {
        const target = chartRef.current;
        if (!target || !hasData)
            return;
        const signature = JSON.stringify({
            dayBands,
            dayRange,
            dayTickText,
            dayTicks,
            height,
            segments,
            trendPoints
        });
        if (signature === plotSignatureRef.current && target.isConnected && isPlotReadyRef.current)
            return;
        plotSignatureRef.current = signature;
        isPlotReadyRef.current = false;
        // Top panel: split the merged trend points into runs coloured by rise/fall direction;
        // not-reporting periods render as a dotted grey line (see buildSocRuns). Any leading/
        // trailing span of the selected range without data is extended as a flat dotted line
        // so those non-reporting days still read as not-reporting rather than a hard line stop.
        const rangeGaps = rangeGapFlags(trendPoints, dayRange);
        const socRuns = withRangeNotReporting(buildSocRuns(trendPoints, segments), trendPoints, dayRange, rangeGaps);
        const socTraces = socRuns.map((run) => ({
            hoverinfo: "skip",
            line: {
                color: run.color,
                dash: run.dash,
                shape: "spline",
                smoothing: SOC_LINE_SMOOTHING,
                width: SOC_LINE_WIDTH
            },
            mode: "lines",
            type: "scatter",
            x: run.x,
            xaxis: "x",
            y: run.y,
            yaxis: "y"
        }));
        // Markers over the trend points are hover targets only — the native Plotly hover box
        // is suppressed (hoverinfo: none) in favour of our own custom tooltip rendered via
        // plotly_hover/plotly_unhover below, which gives full control over its typography,
        // padding and directional arrow to match the figma design.
        // Per figma the only visible dots mark not-reporting transitions: a hollow white
        // circle where reporting stops and a filled black circle where it resumes. Every
        // other point stays transparent so it still works as an invisible hover target
        // without cluttering the line (see buildSocMarkers).
        const socMarkers = buildSocMarkers(trendPoints, segments, rangeGaps);
        const markerTrace = {
            hoverinfo: "none",
            marker: {
                color: socMarkers.colors,
                line: { color: socMarkers.lineColors, width: socMarkers.lineWidths },
                size: socMarkers.sizes
            },
            mode: "markers",
            type: "scatter",
            x: trendPoints.map((point) => point.timestamp),
            xaxis: "x",
            y: trendPoints.map((point) => point.soc),
            yaxis: "y"
        };
        // Alternating day background bands (drawn below everything).
        const bandShapes = dayBands.map((band) => ({
            fillcolor: DAY_BAND_COLOR,
            layer: "below",
            line: { width: 0 },
            type: "rect",
            x0: band.x0,
            x1: band.x1,
            xref: "x",
            y0: SOC_DOMAIN_BOTTOM,
            y1: 1,
            yref: "paper"
        }));
        // Bottom panel: one filled rectangle per activity segment on the operations axis (y2).
        const operationShapes = segments.map((seg) => ({
            fillcolor: getOperationColor(seg.activity ?? seg.assetStatus),
            line: { width: 0 },
            type: "rect",
            x0: seg.start,
            x1: seg.end,
            xref: "x",
            y0: 0,
            y1: 1,
            yref: "y2"
        }));
        // Full-width backdrop behind the Operations strip so any gap between segments (e.g. a
        // still-loading trailing day) reads as a neutral panel rather than plain white.
        const operationsBackgroundShape = {
            fillcolor: "#F2F2F2",
            line: { color: "#E1E1E1", width: 1 },
            type: "rect",
            x0: 0,
            x1: 1,
            xref: "paper",
            y0: 0,
            y1: 1,
            yref: "y2"
        };
        const layout = {
            annotations: [
                {
                    // xanchor "right" so the text's right edge (not left edge) sits a fixed 7px
                    // before the bars start, guaranteeing no overlap regardless of text width.
                    font: { color: "#3F3F3F", family: "Noto Sans, sans-serif", size: 14 },
                    showarrow: false,
                    text: "<span style='font-weight:600;'>Operations</span>",
                    x: 0,
                    xanchor: "right",
                    xref: "paper",
                    xshift: -7,
                    y: OPERATIONS_DOMAIN_TOP / 2,
                    yanchor: "middle",
                    yref: "paper"
                },
                {
                    // Pinned ~15px from the chart container's left edge (margin.l is 85px, so
                    // xshift of -70 leaves 15px), rather than hugging the y-axis tick labels.
                    font: { color: "#3F3F3F", family: "Noto Sans, sans-serif", size: 14 },
                    showarrow: false,
                    text: "<span style='font-weight:600;'>SoC (%)</span>",
                    textangle: -90,
                    x: 0,
                    xanchor: "right",
                    xref: "paper",
                    xshift: -62,
                    y: (SOC_DOMAIN_BOTTOM + 1) / 2,
                    yanchor: "middle",
                    yref: "paper"
                }
            ],
            autosize: true,
            height,
            hovermode: "closest",
            margin: { b: CHART_VERTICAL_MARGIN, l: 85, pad: 0, r: 20, t: CHART_VERTICAL_MARGIN },
            shapes: [...bandShapes, operationsBackgroundShape, ...operationShapes],
            showlegend: false,
            xaxis: {
                anchor: "y",
                domain: [0, 1],
                range: windowRange ? [...windowRange] : undefined,
                showgrid: false,
                tickfont: { color: "#666565", family: "Noto Sans, sans-serif", size: 14 },
                tickmode: "array",
                ticktext: dayTickText,
                tickvals: dayTicks,
                type: "date"
            },
            yaxis: {
                domain: [SOC_DOMAIN_BOTTOM, 1],
                range: [0, SOC_MAX],
                showgrid: false,
                ticklabelstandoff: 10,
                tickmode: "array",
                ticktext: SOC_Y_TICK_LABELS,
                tickvals: SOC_Y_TICKS
            },
            yaxis2: {
                domain: [0, OPERATIONS_DOMAIN_TOP],
                fixedrange: true,
                range: [0, 1],
                showgrid: false,
                showticklabels: false,
                zeroline: false
            }
        };
        const config = {
            displayModeBar: false,
            displaylogo: false,
            doubleClick: false,
            responsive: false
        };
        setTooltip(null);
        setHasPlotError(false);
        resizeObserverRef.current?.disconnect();
        const plotGeneration = ++plotGenerationRef.current;
        Promise.resolve()
            .then(() => Plotly.newPlot(target, [...socTraces, markerTrace], layout, config))
            .then(() => {
            // Ignore completions from stale Plotly render generations.
            if (plotGeneration !== plotGenerationRef.current || !target.isConnected)
                return;
            isPlotReadyRef.current = true;
            const latestRange = latestWindowRangeRef.current;
            if (latestRange) {
                Plotly.relayout(target, { "xaxis.range": [...latestRange] });
            }
            const resizePlot = () => {
                const bounds = target.getBoundingClientRect();
                if (isPlotReadyRef.current && target.isConnected && bounds.width > 0 && bounds.height > 0) {
                    Plotly.Plots.resize(target);
                }
            };
            const observer = new ResizeObserver(resizePlot);
            observer.observe(target);
            resizeObserverRef.current = observer;
            resizePlot();
            // Native listeners (not plotly_hover) so the tooltip + vertical guide line show
            // anywhere the cursor is over the chart, not only directly over a marker point.
            const onMove = (event) => handlePlotMouseMove(event.clientX, event.clientY);
            const onLeave = () => handleUnhover();
            const onWheel = (event) => chartWheelRef.current(event);
            const onDoubleClick = () => chartDoubleClickRef.current();
            target.addEventListener("mousemove", onMove);
            target.addEventListener("mouseleave", onLeave);
            target.addEventListener("wheel", onWheel, { passive: false });
            target.addEventListener("dblclick", onDoubleClick);
            // Scrolling the page (mouse not moving) doesn't fire mouseleave, so the SoC and
            // Operations tooltips would otherwise stay pinned in place while the chart scrolls
            // out from under them. Clear on any scroll (capture: true catches scrolling on
            // inner containers too, not just window).
            window.addEventListener("scroll", onLeave, true);
            // Defensive cleanup in case a newer Plotly render replaced listeners before
            // the previous cleanup effect executed.
            hoverCleanupRef.current?.();
            hoverCleanupRef.current = () => {
                target.removeEventListener("mousemove", onMove);
                target.removeEventListener("mouseleave", onLeave);
                target.removeEventListener("wheel", onWheel);
                target.removeEventListener("dblclick", onDoubleClick);
                window.removeEventListener("scroll", onLeave, true);
            };
        })
            .catch(() => {
            if (plotGeneration === plotGenerationRef.current && target.isConnected) {
                setHasPlotError(true);
            }
        });
    }, [
        trendPoints,
        segments,
        dayBands,
        dayRange,
        dayTickText,
        dayTicks,
        height,
        chartId,
        hasData,
        hasPlotError,
        handleChartDoubleClick,
        handleChartWheel,
        handlePlotMouseMove,
        handleUnhover
    ]);
    useEffect(() => {
        const target = chartRef.current;
        if (!windowRangeStart || !windowRangeEnd || !target?.isConnected || !isPlotReadyRef.current)
            return;
        Plotly.relayout(target, { "xaxis.range": [windowRangeStart, windowRangeEnd] });
    }, [windowRangeEnd, windowRangeStart]);
    useEffect(() => {
        if (!hasData || isError || isLoading)
            return;
        const target = chartRef.current;
        return () => {
            plotGenerationRef.current += 1;
            isPlotReadyRef.current = false;
            hoverCleanupRef.current?.();
            resizeObserverRef.current?.disconnect();
            if (target) {
                Plotly.purge(target);
            }
        };
    }, [hasData, isError, isLoading]);
    const statusContent = renderChartStatus({ hasData, height, isError, isLoading });
    if (statusContent)
        return statusContent;
    return (_jsxs("div", { className: "flex flex-col w-full", "data-test": "ess-soc-operations", children: [_jsxs("div", { className: "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mb-1", "data-test": "ess-soc-line-legend", children: [_jsx("span", { className: "text-[12px] leading-[16px] font-normal text-[#666565] align-bottom", children: "Legend" }), _jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "inline-block w-2 h-2 rounded-full bg-black" }), _jsx("span", { className: "text-[14px] leading-[20px] font-semibold text-[#3F3F3F] align-bottom", children: "Asset On" })] }), _jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "inline-block w-2 h-2 rounded-full border border-black bg-white" }), _jsx("span", { className: "text-[14px] leading-[20px] font-semibold text-[#3F3F3F] align-bottom", children: "Asset Off" })] }), _jsxs("span", { className: "flex items-center gap-1 ml-[46px]", children: [_jsx("span", { className: "relative -top-[2px] inline-block w-2 h-0 border-t-2 border-dotted border-[#CCCCCC]" }), _jsx("span", { className: "text-[14px] leading-[20px] font-semibold text-[#3F3F3F] align-bottom", children: "Not Reporting" })] }), _jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "relative -top-[2px] inline-block w-2 h-0 border-t-2 border-[#FB5A00]" }), _jsx("span", { className: "text-[14px] leading-[20px] font-semibold text-[#3F3F3F] align-bottom", children: "Discharging" })] }), _jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "relative -top-[2px] inline-block w-2 h-0 border-t-2 border-[#1AC69E]" }), _jsx("span", { className: "text-[14px] leading-[20px] font-semibold text-[#3F3F3F] align-bottom", children: "Charging" })] })] }), _jsxs("div", { className: "relative w-full", ref: containerRef, children: [tooltip && (_jsx("img", { alt: "", className: "pointer-events-none absolute z-40 w-auto -translate-x-1/2", "data-test": `${chartId}-hover-line`, src: TimelineVerticalLine, style: {
                            height: `calc(100% - ${CHART_VERTICAL_MARGIN * 2}px)`,
                            left: tooltip.left,
                            top: CHART_VERTICAL_MARGIN
                        } })), tooltip && (_jsxs("div", { className: `pointer-events-none absolute z-50 whitespace-nowrap rounded-md border border-[#E6E6E6] bg-white/90 px-2 py-1.5 shadow-[2px_2px_6px_rgba(0,0,0,0.12)] backdrop-blur-[20px] ${tooltip.placement === "left"
                            ? "[transform:translate(calc(-100%-12px),-50%)]"
                            : "[transform:translate(12px,-50%)]"}`, "data-test": `${chartId}-tooltip`, style: { left: tooltip.left, top: tooltip.top }, children: [_jsxs("div", { className: "flex flex-col gap-1 font-['Roboto',Arial,sans-serif] text-[12px] font-normal leading-[16px] tracking-normal", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-[#565656]", children: "Date and Time:" }), _jsx("span", { className: "text-[#000000]", "data-test": `${chartId}-tooltip-datetime`, children: format(new Date(tooltip.timestamp), "dd.MM.yyyy, hh:mm a") })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-[#565656]", children: "Charging State:" }), _jsx("span", { className: "text-[#000000]", "data-test": `${chartId}-tooltip-charging-state`, children: tooltip.state })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-[#565656]", children: "Asset State:" }), _jsx("span", { className: "text-[#000000]", "data-test": `${chartId}-tooltip-asset-state`, children: tooltip.assetState })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-[#565656]", children: "Utilization:" }), _jsx("span", { className: "text-[#000000]", "data-test": `${chartId}-tooltip-utilization`, children: tooltip.utilization })] })] }), tooltip.placement === "left" ? (_jsx("span", { className: "absolute right-[-9px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[8px] border-y-transparent border-l-[9px] border-l-[#E6E6E6] after:absolute after:right-[2px] after:top-1/2 after:h-0 after:w-0 after:-translate-y-1/2 after:border-y-[7px] after:border-y-transparent after:border-l-[8px] after:border-l-white after:content-['']" })) : (_jsx("span", { className: "absolute left-[-9px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[8px] border-y-transparent border-r-[9px] border-r-[#E6E6E6] after:absolute after:left-[2px] after:top-1/2 after:h-0 after:w-0 after:-translate-y-1/2 after:border-y-[7px] after:border-y-transparent after:border-r-[8px] after:border-r-white after:content-['']" }))] })), tooltip?.opsTitle && (_jsxs("div", { className: `pointer-events-none absolute z-50 whitespace-nowrap rounded-md border border-[#E6E6E6] bg-white/90 px-3 py-2 shadow-[2px_2px_6px_rgba(0,0,0,0.12)] backdrop-blur-[20px] ${tooltip.placement === "left"
                            ? "[transform:translate(calc(-100%-12px),-50%)]"
                            : "[transform:translate(12px,-50%)]"}`, "data-test": `${chartId}-ops-tooltip`, style: { left: tooltip.left, top: tooltip.opsTop }, children: [_jsxs("div", { className: "flex flex-col gap-1 font-['Roboto',Arial,sans-serif] text-[12px] leading-[16px] tracking-normal", children: [_jsx("div", { className: "font-semibold text-[#000000]", "data-test": `${chartId}-ops-tooltip-title`, children: tooltip.opsTitle }), _jsxs("div", { className: "flex items-center gap-1 font-normal", children: [_jsx("span", { className: "text-[#565656]", children: "Started:" }), _jsx("span", { className: "text-[#000000]", "data-test": `${chartId}-ops-tooltip-started`, children: tooltip.opsStarted })] }), _jsxs("div", { className: "flex items-center gap-1 font-normal", children: [_jsx("span", { className: "text-[#565656]", children: "Duration:" }), _jsx("span", { className: "text-[#000000]", "data-test": `${chartId}-ops-tooltip-duration`, children: tooltip.opsDuration })] })] }), tooltip.placement === "left" ? (_jsx("span", { className: "absolute right-[-9px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[8px] border-y-transparent border-l-[9px] border-l-[#E6E6E6] after:absolute after:right-[2px] after:top-1/2 after:h-0 after:w-0 after:-translate-y-1/2 after:border-y-[7px] after:border-y-transparent after:border-l-[8px] after:border-l-white after:content-['']" })) : (_jsx("span", { className: "absolute left-[-9px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[8px] border-y-transparent border-r-[9px] border-r-[#E6E6E6] after:absolute after:left-[2px] after:top-1/2 after:h-0 after:w-0 after:-translate-y-1/2 after:border-y-[7px] after:border-y-transparent after:border-r-[8px] after:border-r-white after:content-['']" }))] })), _jsx("div", { className: "w-full", "data-test": chartId, id: chartId, ref: chartRef })] }), _jsxs("div", { className: "relative z-10 -mt-1 ml-[85px] mr-5 flex items-center justify-between rounded-[4px] border border-[#F2F2F2] bg-white", "data-test": "ess-soc-week-pagination", children: [_jsxs("button", { className: "flex items-center gap-1 disabled:cursor-not-allowed", "data-test": "ess-soc-week-prev", disabled: !canGoPrevWeek, onClick: goToPrevWeek, type: "button", children: [_jsx("span", { className: "flex h-8 w-8 items-start rounded-[2px] bg-[#F2F2F2] p-2", children: _jsx(CatIconArrowBack, { className: canGoPrevWeek
                                        ? "h-[13.193px] w-[13.858px] text-[#1F1F1F]"
                                        : "h-[13.193px] w-[13.191px] text-[#AAAAAA]" }) }), _jsx("span", { className: `font-['Noto_Sans'] text-xs font-normal leading-4 ${canGoPrevWeek ? "text-[#3F3F3F]" : "text-[#AAAAAA]"}`, children: "Previous Week" })] }), _jsx("button", { "aria-label": `Toggle chart zoom. ${weekRangeLabel}`, "aria-live": "polite", className: "font-['Noto_Sans'] text-xs font-normal leading-4 text-[#565656]", "data-test": "ess-soc-week-label", onClick: handleChartDoubleClick, type: "button", children: weekRangeLabel }), _jsxs("button", { className: "flex items-center gap-1 disabled:cursor-not-allowed", "data-test": "ess-soc-week-next", disabled: !canGoNextWeek, onClick: goToNextWeek, type: "button", children: [_jsx("span", { className: `font-['Noto_Sans'] text-xs font-normal leading-4 ${canGoNextWeek ? "text-[#3F3F3F]" : "text-[#AAAAAA]"}`, children: "Next Week" }), _jsx("span", { className: "flex h-8 w-8 items-start rounded-[2px] bg-[#F2F2F2] p-2", children: _jsx(CatIconArrowForward, { className: canGoNextWeek
                                        ? "h-[13.193px] w-[13.858px] text-[#1F1F1F]"
                                        : "h-[13.193px] w-[13.191px] text-[#AAAAAA]" }) })] })] }), _jsx("div", { className: "flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-5", "data-test": "ess-soc-operations-legend", children: OPERATIONS_LEGEND.map((entry) => (_jsxs("span", { className: "flex items-center gap-1", "data-test": `ess-soc-operations-legend-${entry.label}`, children: [_jsx("span", { className: `inline-block w-3 h-3 rounded-[2px] ${entry.swatch}` }), _jsx("span", { className: "text-[14px] leading-[20px] font-semibold text-[#666565] align-bottom", children: entry.label })] }, entry.label))) })] }));
};
export default SocOperationsChart;
